/* eslint-disable max-lines, react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/css'
import { matchPath, Link } from 'react-router-dom'
import { Menu } from 'antd'
import {
  MAIN_FONT,
  FONT_SIZE,
  FONT_WEIGHT,
  NEUTRAL_COLORS,
  Link as CustomLink,
  straitsXTheme,
  MaterialSymbols,
} from '@xfers/design-system'
import {
  Flag as FlagIcon,
  NavDashboard as NavDashboardIcon,
  NavTransactions as NavTransactionsIcon,
  User as UserIcon,
  Invoice as InvoiceIcon,
  CardFilled as CardIcon,
} from '@xfers/design-system/es/icons'
import { useSelector } from 'react-redux'
import sendSolidSrc from '~/assets/sendSolid.svg'
import NewLabel from '~/components/NewLabel'
import { createGaEvent } from '~/helpers/gaHelper'

import { PATHS as APP_PATHS } from '~/app/routes/paths'
import { PATHS as BIZ_PATHS } from '~/biz/routes/paths'
import { PATHS as XIDR_APP_PATHS } from '~/xidrApp/routes/paths'
import { PATHS as XIDR_BIZ_PATHS } from '~/xidrBiz/routes/paths'

import { PERMISSIONS, Route, SubRoute, FeatureMatrixSelectorsType } from '~/types'
import { hasPermission } from '~/permission'
import '~/helpers/String'

import { importStraitsXRoutes } from '~/helpers/dynamicImport'
import { ConfigManager, SITE_TYPE } from '~/managers/ConfigManager'
import { selectFeatureMatrixFactors as bizFeatureMatrix } from '~/biz/store/route/selectors'
import { selectFeatureMatrixFactors as xidrBizFeatureMatrix } from '~/xidrBiz/store/route/selectors'
import { SUPPORT_LINKS, SUPPORT_LINKS_ID } from '~/common/constants'

const { brand } = straitsXTheme

type PathsOfEnum = APP_PATHS | BIZ_PATHS | XIDR_APP_PATHS | XIDR_BIZ_PATHS

type NewPathConfig = {
  path: PathsOfEnum
  platform?: SITE_TYPE
}

const newPaths: NewPathConfig[] = [{ path: BIZ_PATHS.MINT, platform: SITE_TYPE.BIZ }]

const checkIsNewPath = (
  pathToCheck: PathsOfEnum | undefined,
  paths: NewPathConfig[],
  currentPlatform: SITE_TYPE
) => {
  if (!pathToCheck) return false
  return paths.some(
    (config) =>
      config.path === pathToCheck && (!config.platform || config.platform === currentPlatform)
  )
}

const getTabIcon = (isSandbox: boolean, tabName?: string) => {
  const tabIconStyle = {
    marginRight: 10,
    color: isSandbox
      ? straitsXTheme.color.base.onContainer
      : straitsXTheme.brand.secondary.onSurface,
  }

  switch (tabName) {
    case 'Get Started':
      return <FlagIcon style={tabIconStyle} />
    case 'Dashboard':
      return <NavDashboardIcon style={tabIconStyle} />
    case 'Home':
      return <MaterialSymbols icon="home" style={tabIconStyle} />
    case 'Transaction History':
      return <MaterialSymbols icon="receipt_long" style={tabIconStyle} />
    case 'Account Statement':
      return <MaterialSymbols icon="library_books" style={tabIconStyle} />
    case 'Credit Cards':
      return <NavTransactionsIcon style={tabIconStyle} />
    case 'Users':
      return <UserIcon style={tabIconStyle} />
    case 'Developer Tools':
      return <MaterialSymbols icon="logo_dev" style={tabIconStyle} />
    case 'Platform Tools':
      return <MaterialSymbols icon="logo_dev" style={tabIconStyle} />
    case 'Team':
      return <MaterialSymbols icon="group" style={tabIconStyle} />
    case 'Payment Link':
      return <InvoiceIcon style={tabIconStyle} />
    case 'FAQ':
      return <MaterialSymbols icon="help" style={tabIconStyle} />
    case 'Support':
      return <MaterialSymbols icon="headset_mic" style={tabIconStyle} />
    case 'Mint':
      return <MaterialSymbols icon="account_balance_wallet" style={tabIconStyle} />
    case 'Earn':
      return <MaterialSymbols icon="savings" style={tabIconStyle} />
    case 'User Cards':
      return <CardIcon style={tabIconStyle} />
    case 'Cards':
      return <MaterialSymbols icon="credit_card" style={tabIconStyle} />
    case 'DVA Payments':
      return <MaterialSymbols icon="person_apron" style={tabIconStyle} />
    case 'Send':
      return <img src={sendSolidSrc} style={tabIconStyle} />
    default:
      return null
  }
}

const CustomMenuItem: React.FC<{
  tab: {
    tabName?: string
    path?: PathsOfEnum
    permission?: PERMISSIONS
    subTabNames?: SubRoute[]
  }
  itemKey: string
  currentPath: PathsOfEnum
  isSandbox: boolean
  onClickCallback?: Function
  featureMatrix: FeatureMatrixSelectorsType
}> = ({
  tab,
  itemKey,
  currentPath,
  isSandbox,
  children,
  onClickCallback,
  featureMatrix,
  ...props
}) => {
  const matchConfig =
    tab.path === ('/' as PathsOfEnum) ? { path: tab.path, end: true } : { path: tab.path }
  const match = matchPath(matchConfig, currentPath)

  const WithSubMenu = () => {
    if (tab.permission && featureMatrix && !hasPermission(tab.permission, featureMatrix)) {
      return null
    }

    if (tab.subTabNames) {
      const checkPath = (pathNames: SubRoute[], isSandboxProp: boolean) => {
        const paths = pathNames.map((pathName) => pathName.path as PathsOfEnum)
        const matchPathName = paths.some((path) => matchPath({ path }, currentPath))
        return matchPathName ? activeSubMenu(isSandboxProp) : ''
      }
      return (
        <Menu.SubMenu
          title={tab.tabName}
          icon={getTabIcon(isSandbox, tab.tabName)}
          className={checkPath(tab.subTabNames, isSandbox) || SubMenuStyle}
          {...props}
          key={itemKey}
          {...({ eventKey: itemKey } as object)}
        >
          {tab.subTabNames.map((subMenu, index) => SubMenuItem(subMenu, `${itemKey}-${index}`))}
        </Menu.SubMenu>
      )
    }
    return MenuItem()
  }

  const MenuItem = () => {
    const linkStyleClass = isSandbox ? sandBoxActiveLinkClass : activeLinkClass
    const linkClass = match ? linkStyleClass : ''
    const tourClass =
      tab.path === APP_PATHS.LANDING_PAGE ? 'tour-step-4 tour-target-homepage-nav-item' : ''
    const showNewLabel = checkIsNewPath(tab.path, newPaths, ConfigManager.getSiteType())

    const SubMenuTitleComponent = (
      <SubMenuTitle className="no-bg-change-on-select" isSandbox={isSandbox}>
        {getTabIcon(isSandbox, tab.tabName)}
        {tab.tabName}
        {showNewLabel && (
          <NewLabelWrapper>
            <NewLabel />
          </NewLabelWrapper>
        )}
      </SubMenuTitle>
    )

    return (
      <Menu.Item className={`${linkClass} ${tourClass}`} key={itemKey} {...props}>
        {onClickCallback ? (
          <button type="button" className={StyleResetButton} onClick={() => onClickCallback()}>
            {SubMenuTitleComponent}
          </button>
        ) : (
          <Link
            data-cy={`link-${tab.tabName?.removeSpace().toCamelCase()}`}
            style={{ width: '100%' }}
            to={tab.path || '/'}
            onClick={() => {
              createGaEvent(
                'General',
                `click${tab.tabName?.removeSpace()}Link`,
                `Clicked ${tab.tabName} in Left Menu`
              )

              if (tab.path === currentPath && tab.path === '/') {
                window.location.href = tab.path || '/'
              }
            }}
          >
            {SubMenuTitleComponent}
          </Link>
        )}
        {children}
      </Menu.Item>
    )
  }

  const SubMenuItem = (subRoute: SubRoute, indexKey: string) => {
    const subRouteConfig = subRoute.path as PathsOfEnum
    const matchSubRoute = matchPath({ path: subRouteConfig }, currentPath)
    const subMenulinkStyleClass = isSandbox ? sandBoxSubMenuLinkClass : subMenuLinkClass
    return (
      <Menu.Item className={matchSubRoute ? subMenulinkStyleClass : ''} key={indexKey}>
        <Link to={subRoute.path || '/'}>
          <CustomSubMenuItem>{subRoute.sideBar}</CustomSubMenuItem>
        </Link>
      </Menu.Item>
    )
  }

  return WithSubMenu()
}

CustomMenuItem.defaultProps = {
  onClickCallback: undefined,
}

type NavigationMenuProps = {
  currentPathname: string
  displayEarnTab: boolean
  displayGetStartedTab: boolean
  displayMintTab: boolean
  displayAccountStatementTab?: boolean
  displayDevToolTab?: boolean
  displayPlatformToolsTab?: boolean
  displayUserCardsTab?: boolean
  displayCardDashboardTab?: boolean
  displayRfiManagementTab?: boolean
  disableStraitsxMenu?: boolean
  isOnboardingOperator?: boolean
  onClickCallbacks: { [k: string]: Function } | undefined
  isSandbox: boolean
}

const NavigationMenu = (props: NavigationMenuProps) => {
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const [routes, setRoutes] = useState<Route[]>([])

  const siteType = ConfigManager.getSiteType()
  const featureMatrixSelector = siteType === SITE_TYPE.BIZ ? bizFeatureMatrix : xidrBizFeatureMatrix
  const featureMatrix: FeatureMatrixSelectorsType | null = useSelector(
    ConfigManager.isBizSite() ? (featureMatrixSelector as typeof bizFeatureMatrix) : () => null
  )

  useEffect(() => {
    const isChildRoute = routes.some((route) =>
      route.subRoutes?.some((sub) => matchPath({ path: sub.path }, props.currentPathname))
    )
    if (!isChildRoute) {
      setOpenKeys([])
    }
  }, [props.currentPathname, routes])

  useEffect(() => {
    setUpRoutes()
    async function setUpRoutes() {
      let straitsXRoutes = await importStraitsXRoutes()
      if (!props.displayGetStartedTab) {
        // user has completed get started so don't include get started tab
        straitsXRoutes = straitsXRoutes.filter((route) => route.path !== BIZ_PATHS.GET_STARTED)
      }
      if (!props.displayMintTab) {
        straitsXRoutes = straitsXRoutes.filter((route) => route.path !== APP_PATHS.MINT) // Biz & App mint handled here
      }
      if (!props.displayDevToolTab) {
        straitsXRoutes = straitsXRoutes.filter((route) => route.path !== BIZ_PATHS.DEVELOPER_TOOLS)
      }
      if (!props.displayPlatformToolsTab) {
        straitsXRoutes = straitsXRoutes.filter((route) => route.path !== BIZ_PATHS.PLATFORM_TOOLS)
      }
      if (!props.displayEarnTab) {
        straitsXRoutes = straitsXRoutes.filter((route) => route.path !== BIZ_PATHS.EARN)
      }
      if (!props.displayAccountStatementTab) {
        straitsXRoutes = straitsXRoutes.filter(
          (route) => route.path !== BIZ_PATHS.ACCOUNT_STATEMENT
        )
      }
      if (!props.displayCardDashboardTab) {
        straitsXRoutes = straitsXRoutes.filter(
          (route) => route.path !== BIZ_PATHS.CARD_DASHBOARD_SETTLEMENT
        )
      }
      if (!props.displayUserCardsTab) {
        straitsXRoutes = straitsXRoutes.filter((route) => route.path !== BIZ_PATHS.USER_CARDS)
      }
      if (!props.displayRfiManagementTab) {
        straitsXRoutes = straitsXRoutes.filter((route) => route.path !== BIZ_PATHS.RFI_MANAGEMENT)
      }
      if (props.disableStraitsxMenu) {
        straitsXRoutes = straitsXRoutes.filter(
          (route) =>
            route.path === BIZ_PATHS.USER_CARDS ||
            route.path === BIZ_PATHS.CARD_DASHBOARD_SETTLEMENT
        )
      }
      if (props.isOnboardingOperator) {
        straitsXRoutes = straitsXRoutes.filter((route) => route.path !== BIZ_PATHS.LANDING_PAGE)
      }
      setRoutes(straitsXRoutes)
    }
  }, [
    props.displayGetStartedTab,
    props.displayMintTab,
    props.displayEarnTab,
    props.displayDevToolTab,
    props.displayPlatformToolsTab,
    props.displayAccountStatementTab,
    props.displayUserCardsTab,
    props.displayCardDashboardTab,
    props.displayRfiManagementTab,
    props.isOnboardingOperator,
  ])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onOpenChange = (openKey: any[]) => setOpenKeys(openKey)

  const routesHasTab = routes
    .filter((route) => route.sideBar)
    .map(({ path, sideBar, permission, subRoutes }) => {
      return {
        path,
        tabName: sideBar,
        permission,
        subTabNames: subRoutes,
        onClickCallback: path ? props.onClickCallbacks?.[path] : undefined,
      }
    })

  const isXIDRSite = ConfigManager.isXIDRSite()

  const faqLink = SUPPORT_LINKS_ID.HOME
  const supportLink = isXIDRSite ? SUPPORT_LINKS_ID.NEW_REQUEST : SUPPORT_LINKS.SUPPORT_HUB

  return (
    <Container isSandbox={props.isSandbox}>
      <Menu mode="inline" openKeys={openKeys} onOpenChange={onOpenChange}>
        {routesHasTab.map((route, idx) => (
          <CustomMenuItem
            isSandbox={props.isSandbox}
            tab={route}
            key={idx.toString()}
            itemKey={idx.toString()}
            currentPath={props.currentPathname as PathsOfEnum}
            onClickCallback={route.onClickCallback}
            featureMatrix={featureMatrix}
            data-qa-test
          />
        ))}
        <>
          {isXIDRSite && (
            <Menu.Item>
              <StyledCustomLink
                href={faqLink}
                target="_blank"
                onClick={() =>
                  createGaEvent('Landing', 'clickFAQLink', 'Clicked FAQ on Useful Links')
                }
                data-qa-test
                isUnderlined={false}
                isSandbox={props.isSandbox}
              >
                <SubMenuTitle className="no-bg-change-on-select" isSandbox={props.isSandbox}>
                  {getTabIcon(props.isSandbox, 'FAQ')}
                  FAQ
                </SubMenuTitle>
              </StyledCustomLink>
            </Menu.Item>
          )}
          <Menu.Item>
            <StyledCustomLink
              href={supportLink}
              target="_blank"
              onClick={() =>
                createGaEvent(
                  'Landing',
                  'clickCustomerSupportLink',
                  'Clicked Create Customer Support Link on Useful Links'
                )
              }
              isUnderlined={false}
              isSandbox={props.isSandbox}
            >
              <SubMenuTitle className="no-bg-change-on-select" isSandbox={props.isSandbox}>
                {getTabIcon(props.isSandbox, 'Support')}
                Support
              </SubMenuTitle>
            </StyledCustomLink>
          </Menu.Item>
        </>
      </Menu>
    </Container>
  )
}

NavigationMenu.defaultProps = {
  displayAccountStatementTab: false,
  displayDevToolTab: false,
  displayUserCardsTab: false,
  displayCardDashboardTab: false,
  displayRfiManagementTab: false,
  disableStraitsxMenu: false,
}

export default React.memo(NavigationMenu)

const sandBoxOrMainColor = (props: { isSandbox: boolean; isSubMenu?: boolean }) =>
  props.isSandbox ? straitsXTheme.color.base.onContainer : straitsXTheme.brand.secondary.onSurface

const StyledCustomLink = styled(CustomLink)`
  display: block;
  width: 100%;
  & .material-symbols-rounded {
    color: ${sandBoxOrMainColor} !important;
  }
`

const SubMenuTitle = styled.div`
  transition: 0.2s all ease !important;
  font-family: ${MAIN_FONT};
  font-weight: ${FONT_WEIGHT.REGULAR};
  font-size: ${FONT_SIZE.P1};
  color: ${sandBoxOrMainColor};
  padding-left: ${(props: { isSubMenu?: boolean }) => (props.isSubMenu ? '5px' : '15px')};
  padding-right: ${(props: { isSubMenu?: boolean }) => (props.isSubMenu ? 0 : '15px')};
  display: flex;
  align-items: center;
  border-radius: 5px;
  height: 50px;
`

const NewLabelWrapper = styled.div`
  margin-left: auto;
`

const CustomSubMenuItem = styled.div`
  font-family: ${MAIN_FONT};
  font-weight: ${FONT_WEIGHT.MEDIUM};
  font-size: ${FONT_SIZE.P1};
  color: ${straitsXTheme.brand.secondary.onSurface};
  padding-left: 5px;
  border-radius: 5px;
  .ant-menu-item &:hover {
    background-color: ${straitsXTheme.color.base.onContainer};
    color: ${straitsXTheme.color.surface.dark};
  }
  .ant-menu-item &:hover path {
    fill: ${straitsXTheme.color.base.primary} !important;
  }
`

const SubMenuStyle = css`
  transition: 0.3s all ease !important;
  font-family: ${MAIN_FONT};
  font-weight: ${FONT_WEIGHT.MEDIUM};
  font-size: ${FONT_SIZE.P1};
  color: ${NEUTRAL_COLORS.G400};
  border-radius: 5px;
`

const Container = styled.div`
  padding-bottom: 45px;
  & .ant-menu-inline {
    border: 0;
    background: ${(props: { isSandbox: boolean }) =>
      props.isSandbox
        ? straitsXTheme.brand.primary.contrast
        : straitsXTheme.color.base.onContainer};
  }
  & .ant-menu-inline > .ant-menu-submenu > .ant-menu-submenu-title {
    height: 50px;
    margin: 0;
    padding-left: 14px !important;
  }
  & .ant-menu-inline > .ant-menu-submenu {
    padding-top: 7px;
    padding-bottom: 7px;
  }

  & .ant-menu-inline .ant-menu-item {
    height: unset;
    width: 100%;
    margin: 0;
    padding-bottom: 7px;
    padding-top: 7px;
    padding-left: 20px !important;
    padding-right: 20px !important;
    :hover {
      background-color: unset !important;

      ${SubMenuTitle} {
        background-color: ${(props: { isSandbox: boolean }) =>
          props.isSandbox ? '#005755' : '#D7EFE2'} !important;
      }
    }
  }

  & .ant-menu-inline .ant-menu-submenu {
    height: unset;
    margin: 0 0 8px 0;
    padding-bottom: 7px;
    padding-top: 7px;
    padding-left: 20px !important;
    padding-right: 20px !important;
    width: calc(100% + 1px);
  }

  & .ant-menu-submenu-title {
    display: flex;
    align-items: center;
    border-radius: 5px;
    color: ${sandBoxOrMainColor};
    :hover {
      color: ${(props: { isSandbox: boolean }): string =>
        props.isSandbox ? straitsXTheme.color.base.onContainer : straitsXTheme.color.surface.dark};
      background-color: ${(props: { isSandbox: boolean }): string =>
        props.isSandbox ? brand.action.default : straitsXTheme.color.base.onContainer};
    }
  }
  & .ant-menu-submenu-title .ant-menu-item-icon + span,
  & .ant-menu-submenu-title .anticon + span {
    margin-inline-start: 0 !important;
  }
  & .ant-menu-submenu-title .ant-menu-title-content {
    color: ${sandBoxOrMainColor} !important;
  }

  & .ant-menu-submenu-inline > .ant-menu-submenu-title:hover .ant-menu-submenu-arrow::after {
    background: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox ? straitsXTheme.color.base.onContainer : straitsXTheme.color.surface.dark};
  }
  & .ant-menu-submenu-inline > .ant-menu-submenu-title:hover .ant-menu-submenu-arrow::before {
    background: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox ? straitsXTheme.color.base.onContainer : straitsXTheme.color.surface.dark};
  }
  & .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow::after {
    background: ${sandBoxOrMainColor};
  }
  & .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow::before {
    background: ${sandBoxOrMainColor};
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
    background: transparent;
  }
  & .ant-menu-inline .ant-menu-item-selected::after {
    display: none;
  }
  &
    .ant-menu:not(.ant-menu-horizontal)
    .ant-menu-item-selected
    ${SubMenuTitle}:not(.no-bg-change-on-select) {
    background-color: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox ? brand.primary.default : 'white'};
    color: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox ? straitsXTheme.color.base.onContainer : straitsXTheme.color.surface.dark};
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected ${CustomSubMenuItem} {
    background-color: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox ? brand.primary.default : '#d7efe2'};
    color: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox ? straitsXTheme.color.base.onContainer : '#257c58'};
    font-weight: ${FONT_WEIGHT.BOLD};
  }
  & .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow {
    right: 30px;
    line-height: 50px;
  }
  & .ant-menu-sub.ant-menu-inline > .ant-menu-item {
    height: 54px;
    padding: 2.5px 0 2.5px 40px !important;
    margin: 0;
  }
  & .ant-menu-submenu > .ant-menu {
    background: ${(props: { isSandbox: boolean }) =>
      props.isSandbox
        ? straitsXTheme.brand.primary.contrast
        : straitsXTheme.color.base.onContainer};
  }
  & .ant-menu-item > a::before {
    display: none;
  }
  & .ant-menu-inline .ant-menu-item:not(:last-child) {
    margin-bottom: 8px;
  }
  & .ant-menu-submenu-selected > .ant-menu-submenu-title {
    font-family: ${MAIN_FONT};
    font-weight: ${FONT_WEIGHT.MEDIUM};
    font-size: ${FONT_SIZE.P1};
    color: ${sandBoxOrMainColor} !important;
    background-color: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox
        ? straitsXTheme.brand.primary.contrast
        : straitsXTheme.color.base.onContainer};
  }
  & .ant-menu-submenu-selected > .ant-menu-submenu-title .ant-menu-submenu-arrow::after {
    background: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox
        ? straitsXTheme.color.base.onContainer
        : straitsXTheme.color.surface.dark} !important;
  }
  & .ant-menu-submenu-selected > .ant-menu-submenu-title .ant-menu-submenu-arrow::before {
    background: ${(props: { isSandbox: boolean }): string =>
      props.isSandbox
        ? straitsXTheme.color.base.onContainer
        : straitsXTheme.color.surface.dark} !important;
  }
  @media (max-width: 768px) {
    & .ant-menu-submenu-open > .ant-menu-submenu-title {
      font-family: ${MAIN_FONT};
      font-weight: ${FONT_WEIGHT.MEDIUM};
      font-size: ${FONT_SIZE.P1};
      color: ${(props: { isSandbox: boolean }): string =>
        props.isSandbox
          ? straitsXTheme.color.base.onContainer
          : straitsXTheme.color.surface.dark} !important;
      background-color: ${(props: { isSandbox: boolean }): string =>
        props.isSandbox ? brand.primary.default : straitsXTheme.color.base.onContainer};
      .ant-menu-submenu-arrow::after,
      .ant-menu-submenu-arrow::before {
        background: ${(props: { isSandbox: boolean }): string =>
          props.isSandbox
            ? straitsXTheme.color.base.onContainer
            : straitsXTheme.color.surface.dark} !important;
      }
    }
  }
`

const activeLinkClass = css`
  &.ant-menu-item ${SubMenuTitle} {
    background-color: #d7efe2;
    color: #257c58;
    font-weight: ${FONT_WEIGHT.BOLD};
  }
  &.ant-menu-item ${SubMenuTitle} svg {
    color: #257c58 !important;
  }
  &:hover {
    background-color: inherit !important;
  }
  &:hover ${SubMenuTitle} {
    background-color: #d7efe2;
    color: #257c58;
  }
  &:hover ${SubMenuTitle} svg {
    color: #257c58 !important;
  }
`

const sandBoxActiveLinkClass = css`
  &.ant-menu-item ${SubMenuTitle} {
    background-color: #005755;
    color: white;
    font-weight: ${FONT_WEIGHT.BOLD};
  }
  &.ant-menu-item ${SubMenuTitle} svg {
    color: white !important;
  }
  &:hover ${SubMenuTitle} {
    background-color: #005755;
    color: white;
  }
  &:hover ${SubMenuTitle} svg {
    color: white !important;
  }
`

const subMenuLinkClass = css`
  &.ant-menu-item ${CustomSubMenuItem} {
    background-color: #d7efe2;
    color: #257c58;
    font-weight: ${FONT_WEIGHT.BOLD};
  }
  &.ant-menu-item ${CustomSubMenuItem} path {
    fill: #257c58 !important;
  }
`
const sandBoxSubMenuLinkClass = css`
  &.ant-menu-item ${CustomSubMenuItem} {
    background-color: ${brand.primary.default};
    color: white;
  }
  &.ant-menu-item ${CustomSubMenuItem} path {
    fill: white !important;
  }
`

const activeSubMenu = (isSandbox: boolean) => css`
  & .ant-menu-submenu-title {
    font-family: ${MAIN_FONT};
    font-weight: ${FONT_WEIGHT.MEDIUM};
    font-size: ${FONT_SIZE.P1};
    color: ${isSandbox
      ? straitsXTheme.color.base.onContainer
      : straitsXTheme.color.surface.dark} !important;
    background-color: ${isSandbox ? brand.primary.default : straitsXTheme.color.base.onContainer};
    .ant-menu-submenu-arrow::after,
    .ant-menu-submenu-arrow::before {
      background: ${isSandbox
        ? straitsXTheme.color.base.onContainer
        : straitsXTheme.color.surface.dark} !important;
    }
  }
`

const StyleResetButton = css`
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
`
